<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';

import PipelineForm from './PipelineForm.vue';
import {
  buildPipelineTemplates,
  getStageColorClasses,
  normalizePipelineTemplateData,
  stageColorStyle,
} from './pipelineTemplates';

defineProps({
  canManage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['switch-view', 'pipeline-created']);
const { t } = useI18n();

const showNewPipelineModal = ref(false);
const showTemplatesModal = ref(false);
const selectedTemplateId = ref('');
const newPipelineFormRef = ref(null);

const pipelineTemplates = computed(() => buildPipelineTemplates(t));
const selectedTemplate = computed(() =>
  pipelineTemplates.value.find(
    template => template.id === selectedTemplateId.value
  )
);

const emptyTemplate = () => ({
  name: '',
  description: '',
  stages: {},
  settings: { agents: [] },
});
const newPipelineInitialData = ref(emptyTemplate());

const openTemplateLibrary = () => {
  selectedTemplateId.value = '';
  showTemplatesModal.value = true;
};

const openBlankPipeline = () => {
  selectedTemplateId.value = '';
  newPipelineInitialData.value = emptyTemplate();
  showNewPipelineModal.value = true;
};

const closeTemplateLibrary = () => {
  showTemplatesModal.value = false;
};

const handleTemplateSelect = templateId => {
  selectedTemplateId.value = templateId;
};

const useSelectedTemplate = () => {
  if (!selectedTemplate.value) return;

  newPipelineInitialData.value = normalizePipelineTemplateData(
    selectedTemplate.value
  );
  closeTemplateLibrary();
  showNewPipelineModal.value = true;
};

const handleBack = () => {
  /* eslint-disable-next-line vue/custom-event-name-casing -- public component event */
  emit('switch-view', 'pipeline');
};

const handlePipelineCreated = pipeline => {
  showNewPipelineModal.value = false;
  selectedTemplateId.value = '';
  /* eslint-disable-next-line vue/custom-event-name-casing -- public component event */
  emit('pipeline-created', pipeline);
};

const requestNewPipelineModalClose = () =>
  newPipelineFormRef.value?.requestClose?.() ?? true;
</script>

<template>
  <header
    class="flex flex-col flex-shrink-0 gap-3 px-4 py-3 border-b border-n-weak bg-n-background sm:min-h-16 sm:flex-row sm:items-center sm:justify-between sm:px-6"
  >
    <div class="flex items-center min-w-0 gap-2">
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="lg"
        icon="i-lucide-chevron-left"
        :aria-label="t('PIPELINE.PIPELINES.MANAGER.BACK')"
        :title="t('PIPELINE.PIPELINES.MANAGER.BACK')"
        class="flex-shrink-0"
        @click="handleBack"
      />
      <div class="min-w-0">
        <h1 class="text-lg font-semibold leading-6 truncate text-n-slate-12">
          {{ t('PIPELINE.PIPELINES.TITLE') }}
        </h1>
        <p class="hidden text-sm text-n-slate-10 sm:block">
          {{ t('PIPELINE.PIPELINES.MANAGER.SUBTITLE') }}
        </p>
      </div>
    </div>

    <div v-if="canManage" class="grid grid-cols-2 gap-2 sm:flex">
      <Button
        type="button"
        variant="outline"
        color="slate"
        size="lg"
        icon="i-lucide-layout-template"
        @click="openTemplateLibrary"
      >
        {{ t('PIPELINE.TEMPLATES_LIBRARY.TRIGGER_LABEL') }}
      </Button>
      <Button
        type="button"
        variant="solid"
        color="blue"
        size="lg"
        icon="i-lucide-plus"
        @click="openBlankPipeline"
      >
        {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
      </Button>
    </div>

    <Modal
      v-model:show="showTemplatesModal"
      size="large"
      initial-focus-selector="[data-template-option]"
      @close="closeTemplateLibrary"
    >
      <div class="flex flex-col min-h-full">
        <div class="px-5 pt-6 pb-4 pr-16 sm:px-8 sm:pt-8 sm:pr-20">
          <h2
            data-modal-title
            class="text-xl font-semibold text-n-slate-12 sm:text-2xl"
          >
            {{ t('PIPELINE.TEMPLATES_LIBRARY.TITLE') }}
          </h2>
          <p class="mt-2 text-sm text-n-slate-10 sm:text-base">
            {{ t('PIPELINE.TEMPLATES_LIBRARY.SUBTITLE') }}
          </p>
        </div>

        <div
          class="grid grid-cols-1 gap-3 px-5 pb-5 sm:px-8 md:grid-cols-2"
          role="group"
          :aria-label="t('PIPELINE.TEMPLATES_LIBRARY.OPTIONS_LABEL')"
        >
          <button
            v-for="(template, index) in pipelineTemplates"
            :key="template.id"
            type="button"
            :data-template-option="index === 0 ? '' : undefined"
            :aria-pressed="selectedTemplateId === template.id"
            class="min-h-28 p-4 text-left border rounded-xl outline-none transition-colors focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-background"
            :class="
              selectedTemplateId === template.id
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak bg-n-solid-2 hover:border-n-strong hover:bg-n-alpha-1'
            "
            @click="handleTemplateSelect(template.id)"
          >
            <div class="flex items-start gap-3">
              <div
                class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl bg-n-alpha-2 text-n-slate-11"
                aria-hidden="true"
              >
                <FluentIcon
                  :icon="template.icon"
                  size="22"
                  :icons="dashboardIcons"
                />
              </div>
              <div class="min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <h3 class="font-semibold leading-5 text-n-slate-12">
                    {{ template.name }}
                  </h3>
                  <span
                    v-if="selectedTemplateId === template.id"
                    class="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-n-brand text-white"
                    aria-hidden="true"
                  >
                    <FluentIcon
                      icon="checkmark"
                      size="14"
                      :icons="dashboardIcons"
                    />
                  </span>
                </div>
                <p class="mt-1 text-sm leading-5 text-n-slate-10">
                  {{ template.description }}
                </p>
              </div>
            </div>

            <div class="flex flex-wrap gap-1.5 mt-4" aria-hidden="true">
              <span
                v-for="stage in Object.values(template.stages)"
                :key="stage.id"
                class="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border rounded-md"
                :class="getStageColorClasses(stage.color).badge"
                :style="stageColorStyle(stage.color, 'badge')"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :class="getStageColorClasses(stage.color).dot"
                  :style="stageColorStyle(stage.color, 'dot')"
                />
                {{ stage.name }}
              </span>
            </div>
            <span class="sr-only">
              {{
                t('PIPELINE.TEMPLATES_LIBRARY.STAGES_SUMMARY', {
                  count: Object.keys(template.stages).length,
                })
              }}
            </span>
          </button>
        </div>

        <div
          class="sticky bottom-0 flex flex-col-reverse gap-2 px-5 py-4 mt-auto border-t bg-n-background/95 border-n-weak backdrop-blur-sm sm:flex-row sm:justify-end sm:px-8"
        >
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="lg"
            @click="closeTemplateLibrary"
          >
            {{ t('PIPELINE.CANCEL') }}
          </Button>
          <Button
            type="button"
            variant="solid"
            color="blue"
            size="lg"
            icon="i-lucide-arrow-right"
            trailing-icon
            :disabled="!selectedTemplate"
            @click="useSelectedTemplate"
          >
            {{ t('PIPELINE.TEMPLATES_LIBRARY.USE_TEMPLATE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model:show="showNewPipelineModal"
      size="full"
      :before-close="requestNewPipelineModalClose"
      @close="showNewPipelineModal = false"
    >
      <div class="p-5 sm:p-8">
        <h2 data-modal-title class="mb-4 text-xl font-semibold text-n-slate-12">
          {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
        </h2>
        <PipelineForm
          ref="newPipelineFormRef"
          :initial-data="newPipelineInitialData"
          @saved="handlePipelineCreated"
          @close="showNewPipelineModal = false"
        />
      </div>
    </Modal>
  </header>
</template>
